import React, { useState } from 'react';
import './LicenseFormModal.css';

const emptySystem = () => ({ systemName: '' });

const LicenseFormModal = ({ onSubmit, onCancel, showAlert, applicationData }) => {
  const [customerType, setCustomerType] = useState('individual');
  const [formData, setFormData] = useState({
    clientName: '',
    email: '',
    clinicName: '',
    phone: '',
    city: '',
    expiryDays: 30,
    notes: '',
    offlineGraceHours: 72,
    applicationId: '',
  });
  const [orgData, setOrgData] = useState({
    orgName: '',
    contactPerson: '',
    phone: '',
    city: '',
    email: '',
  });
  const [systems, setSystems] = useState([emptySystem()]);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (customerType === 'organisation') {
        const cleanedSystems = systems.map((s) => s.systemName.trim()).filter(Boolean);
        if (!orgData.orgName.trim() || !orgData.email.trim()) {
          showAlert('Organisation name and email are required', 'error');
          return;
        }
        if (cleanedSystems.length < 1) {
          showAlert('Add at least one system', 'error');
          return;
        }
        await onSubmit({
          customerType: 'organisation',
          orgName: orgData.orgName.trim(),
          contactPerson: orgData.contactPerson.trim(),
          phone: orgData.phone.trim(),
          city: orgData.city.trim(),
          email: orgData.email.trim(),
          expiryDays: formData.expiryDays,
          notes: formData.notes,
          applicationId: formData.applicationId,
          offlineGraceHours: formData.offlineGraceHours,
          systems: cleanedSystems.map((systemName) => ({ systemName })),
        });
      } else {
        await onSubmit({
          customerType: 'individual',
          ...formData,
        });
      }
    } catch (error) {
      showAlert(`Failed to create license: ${error.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="license-form-overlay" onClick={onCancel}>
      <div className="license-form-modal license-form-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="license-form-header">
          <h2>🎫 Generate New License</h2>
          <button onClick={onCancel} className="license-form-close">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="license-form">
          <div className="license-type-toggle">
            <label className={`type-option ${customerType === 'individual' ? 'active' : ''}`}>
              <input
                type="radio"
                name="customerType"
                value="individual"
                checked={customerType === 'individual'}
                onChange={() => setCustomerType('individual')}
              />
              Individual
            </label>
            <label className={`type-option ${customerType === 'organisation' ? 'active' : ''}`}>
              <input
                type="radio"
                name="customerType"
                value="organisation"
                checked={customerType === 'organisation'}
                onChange={() => setCustomerType('organisation')}
              />
              Organisation
            </label>
          </div>

          {customerType === 'individual' ? (
            <>
              <div className="license-form-info">
                <strong>Client Name *</strong>
                <input type="text" value={formData.clientName} onChange={(e) => setFormData({ ...formData, clientName: e.target.value })} required className="license-form-input" />
              </div>
              <div className="license-form-info">
                <strong>Email Address *</strong>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="license-form-input" />
              </div>
              <div className="license-form-info">
                <strong>Clinic Name</strong>
                <input type="text" value={formData.clinicName} onChange={(e) => setFormData({ ...formData, clinicName: e.target.value })} className="license-form-input" />
              </div>
              <div className="license-form-info">
                <strong>Phone</strong>
                <input type="text" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="license-form-input" />
              </div>
              <div className="license-form-info">
                <strong>City</strong>
                <input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="license-form-input" />
              </div>
            </>
          ) : (
            <>
              <div className="license-form-info">
                <strong>Organisation Name *</strong>
                <input type="text" value={orgData.orgName} onChange={(e) => setOrgData({ ...orgData, orgName: e.target.value })} required className="license-form-input" />
              </div>
              <div className="license-form-info">
                <strong>Contact Person</strong>
                <input type="text" value={orgData.contactPerson} onChange={(e) => setOrgData({ ...orgData, contactPerson: e.target.value })} className="license-form-input" />
              </div>
              <div className="license-form-info">
                <strong>Email *</strong>
                <input type="email" value={orgData.email} onChange={(e) => setOrgData({ ...orgData, email: e.target.value })} required className="license-form-input" />
              </div>
              <div className="license-form-info">
                <strong>Phone</strong>
                <input type="text" value={orgData.phone} onChange={(e) => setOrgData({ ...orgData, phone: e.target.value })} className="license-form-input" />
              </div>
              <div className="license-form-info">
                <strong>City</strong>
                <input type="text" value={orgData.city} onChange={(e) => setOrgData({ ...orgData, city: e.target.value })} className="license-form-input" />
              </div>

              <div className="systems-section">
                <div className="systems-section-header">
                  <strong>Systems *</strong>
                  <button
                    type="button"
                    className="systems-add-btn"
                    onClick={() => setSystems([...systems, emptySystem()])}
                  >
                    + Add System
                  </button>
                </div>
                {systems.map((system, index) => (
                  <div key={index} className="system-row">
                    <input
                      type="text"
                      placeholder='e.g. "Radiology PC"'
                      value={system.systemName}
                      onChange={(e) => {
                        const next = [...systems];
                        next[index] = { systemName: e.target.value };
                        setSystems(next);
                      }}
                      className="license-form-input"
                      required={index === 0}
                    />
                    {systems.length > 1 && (
                      <button
                        type="button"
                        className="system-remove-btn"
                        onClick={() => setSystems(systems.filter((_, i) => i !== index))}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="license-form-info">
            <strong>Validity</strong>
            <select
              value={formData.expiryDays}
              onChange={(e) => setFormData({ ...formData, expiryDays: parseInt(e.target.value, 10) })}
              required
              className="license-form-select"
            >
              {[1, 3, 6, 12, 24, 36].map((months) => (
                <option key={months} value={months * 30}>
                  {months} month{months > 1 ? 's' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="license-form-info">
            <strong>Offline Grace (hours)</strong>
            <input
              type="number"
              min="1"
              max="720"
              value={formData.offlineGraceHours}
              onChange={(e) => setFormData({ ...formData, offlineGraceHours: parseInt(e.target.value, 10) || 72 })}
              className="license-form-input"
            />
          </div>

          <div className="license-form-info">
            <strong>Application Name *</strong>
            <select
              value={formData.applicationId || ''}
              onChange={(e) => setFormData({ ...formData, applicationId: e.target.value })}
              required
              className="license-form-select"
            >
              <option value="" disabled>Select an application</option>
              {applicationData?.map((app) => (
                <option key={app.id} value={app.id}>{app.application_name}</option>
              ))}
            </select>
          </div>

          <div className="license-form-info">
            <strong>Notes (Optional)</strong>
            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows="4" className="license-form-textarea" />
          </div>

          <div className="license-form-actions">
            <button type="button" onClick={onCancel} disabled={submitting} className="license-form-btn-cancel">Cancel</button>
            <button type="submit" disabled={submitting} className="license-form-btn-submit">
              {submitting ? '⏳ Generating...' : customerType === 'organisation' ? '✨ Create Organisation Licenses' : '✨ Generate License Key'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LicenseFormModal;
